import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";
import {fetchAllPages} from "../../fetchAllPages.ts";

export enum RecordingMode {
    USER_CONTROLLED = 'USER_CONTROLLED',
    RESTRICTED = 'RESTRICTED',
}

export interface Msisdn {
    "msisdnId": number,
    "assigmentScope"?: string,
    "msisdn": number,
    "label": string,
    "icon": string,
    "type": string,
    "iconColor": string,
    "recordingMode"?: RecordingMode,
    "reservedFor"?: string | null,
}
export interface MsisdnSelectItem extends Msisdn {}

export type PhoneNumberApiItem = Readonly<{
    id?: number;
    msisdnId?: number;
    phoneNumber?: number;
    msisdn?: number;
    label: string;
    icon: string;
    type: string;
    iconColor: string;
    assignmentScope?: string;
    assigmentScope?: string;
    recordingMode?: RecordingMode;
    reservedFor?: string | null;
}>;

const requirePhoneNumberField = (value: number | undefined, field: string): number => {
    if (value === undefined) {
        throw new Error(`Missing phone number field: ${field}`);
    }

    return value;
};

export const normalizePhoneNumber = (phoneNumber: PhoneNumberApiItem): Msisdn => ({
    msisdnId: requirePhoneNumberField(phoneNumber.msisdnId ?? phoneNumber.id, 'id'),
    assigmentScope: phoneNumber.assigmentScope ?? phoneNumber.assignmentScope,
    msisdn: requirePhoneNumberField(phoneNumber.msisdn ?? phoneNumber.phoneNumber, 'phoneNumber'),
    label: phoneNumber.label,
    icon: phoneNumber.icon,
    type: phoneNumber.type,
    iconColor: phoneNumber.iconColor,
    recordingMode: phoneNumber.recordingMode,
    reservedFor: phoneNumber.reservedFor,
});

export const normalizePhoneNumbers = (phoneNumbers: readonly PhoneNumberApiItem[]): Msisdn[] =>
    phoneNumbers.map(normalizePhoneNumber);

export interface MsisdnMissedCalls {
    userId: number;
    phoneNumberId: number;
    phoneNumber: string;
    totalDndLastMissingCalls: number;
}
export interface DnDNumbersResponse {
    userId: number;
    phoneNumbers: PhoneNumberDndStatus[];
    dueDate: string | undefined;
    enabled: boolean;
}
export interface PhoneNumberDndStatus {
    phoneNumberId: number;
    enabled: boolean;
    dueDate: string | undefined;
}
export const msisdnsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getUserMsisdns: builder.query<{ data: MsisdnSelectItem[]; totalCount: number }, { phrase:string  | undefined, userId: string | null }>({
            queryFn: async ({phrase, userId}, _queryApi, _extraOptions, baseQuery) => {
                const result = await fetchAllPages<PhoneNumberApiItem>(
                    baseQuery,
                    ({limit, offset}) => ({
                        url: `/api/v1/phone-numbers`,
                        params: { userId, phrase, limit, offset },
                    }),
                    (data) => unwrapData(data as { data: PhoneNumberApiItem[] }) ?? [],
                );
                if ('error' in result) {
                    return {error: result.error};
                }
                return {data: {data: normalizePhoneNumbers(result.items), totalCount: result.totalCount}};
            },
            providesTags: (_result, _error, arg) => [{ type: 'Numbers', id: Number(arg.userId) }],
        }),
        getPhoneNumberDndStatus: builder.query<DnDNumbersResponse, { userId: any }>({
            query: ({userId}) => ({
                url: `/api/v1/users/${userId}/phone-numbers/dnd`,
                method: 'GET',
            }),
            providesTags: [{ type: 'PhoneNumbersDnd' }],
        }),
        togglePhooneNumberDnd: builder.mutation<any, {dueDate?: string | Date | null, enabled:boolean, userId: any, phoneNumberId: number }>({
            query: ({enabled, userId, dueDate, phoneNumberId}) => {
                if (!userId) throw new Error('User ID not found');
                return {
                    url: `/api/v1/users/${userId}/phone-numbers/${phoneNumberId}/dnd`,
                    method: 'PUT',
                    body: { enabled, dueDate },
                };
            },
            invalidatesTags: [{ type: 'PhoneNumbersDnd' }],
        }),
    }),
});


export const {
    useGetUserMsisdnsQuery,
    useGetPhoneNumberDndStatusQuery,
    useTogglePhooneNumberDndMutation
} = msisdnsApi;
